import { devtoolsExchange } from '@urql/devtools';
import type { Exchange } from 'urql';
import { Client, fetchExchange } from 'urql';
import authExchange from '@/client/exchanges/authExchange';
import cacheExchange from '@/client/exchanges/cacheExchange';
import errorExchange from '@/client/exchanges/errorExchange';
import localMutationExchange from '@/client/exchanges/localMutationExchange';
import retryExchange from '@/client/exchanges/retryExchange';
import subscriptionExchange, {
  resetSubscriptionClient,
} from '@/client/exchanges/subscriptionExchange';

const globalKey = '__1timeUrqlClients__' as const;

type ClientKey = 'auth' | 'external' | 'private';

type UrqlClientGlobal = typeof globalThis & {
  [key in typeof globalKey]?: Map<ClientKey, Client>;
};

type CreateClientOptions = {
  key: ClientKey;
  exchanges: Exchange[];
};

function createUrqlClient({ exchanges }: CreateClientOptions) {
  if (!process.env.EXPO_PUBLIC_GRAPHQL_HTTP_URL) {
    throw new Error(
      'EXPO_PUBLIC_GRAPHQL_HTTP_URL must be defined to create a new urql http client.',
    );
  }

  return new Client({
    url: process.env.EXPO_PUBLIC_GRAPHQL_HTTP_URL,
    preferGetMethod: false,
    exchanges,
  });
}

function getClientMap() {
  const typedGlobal = globalThis as UrqlClientGlobal;

  if (!typedGlobal[globalKey]) {
    typedGlobal[globalKey] = new Map();
  }

  return typedGlobal[globalKey];
}

export function getUrqlClient(options: CreateClientOptions) {
  const clientMap = getClientMap();
  const existing = clientMap.get(options.key);

  if (existing) {
    return existing;
  }

  const created = createUrqlClient(options);
  clientMap.set(options.key, created);

  return created;
}

export function resetUrqlClient(options: CreateClientOptions) {
  const clientMap = getClientMap();

  if (options.key === 'private') {
    resetSubscriptionClient();
  }

  const created = createUrqlClient(options);
  clientMap.set(options.key, created);

  return created;
}

export const authExchanges = [fetchExchange];

export const externalExchanges = [cacheExchange, fetchExchange];

export const privateExchanges = [
  devtoolsExchange,
  cacheExchange,
  localMutationExchange,
  errorExchange,
  retryExchange,
  authExchange,
  fetchExchange,
  subscriptionExchange,
];
